import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "reactstrap";
import {
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import moment from "moment";
import { withTranslation } from "react-i18next";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import { usePremiseTypeQuery } from "apis/PremiseType";
import {
  minHtContractDemandLoad,
  premiseNamesForTimeShift,
} from "common/constant";
import DateField from "Components/Common/DatePicker";
import { loadOptions, timeshiftingOptions } from "common/enums";
import { onlyNumberKeys } from "utils";

const SINGLE_PHASE = 1;
const THREE_PHASE = 2;

const htLoadOptions = [{ value: THREE_PHASE, label: "Three Phase" }];

const tentionOptions = [
  { value: "LT", label: "LT" },
  { value: "HT", label: "HT" },
];

const premiseNames = ["temporary"];

const unitOptions = [
  { value: 1, label: "kW" },
  { value: 2, label: "kVA" },
  { value: 3, label: "HP" },
];

const singlePhaseOptions = [{ value: 240, label: "240 V" }];

const tomorrow = moment().add(1, "days");

const ConnectionLoadDetails = ({ formik, t }) => {
  const {
    values: {
      connectionLoad: {
        powerRequirement,
        loadDetails,
        tention,
        isGreenPowerRequired,
        supplyVoltage,
        expectedConnectionEndDate,
        expectedConnectionStartDate,
        timeShift,
      },
    },
    values,
    setFieldValue,
  } = formik;

  const [premiseIdsForTimeShift, setPremiseIdsForTimeShift] = useState([]);
  const [premiseIds, setPremiseIds] = useState([]);
  const [supplyVoltageOptions, setSupplyVoltageOptions] = useState([]);

  const threePhaseOptions = useMemo(
    () =>
      tention === tentionOptions[1].value
        ? [
            { value: 33, label: "33 kV" },
            { value: 11, label: "11 kV" },
            { value: 415, label: "415 V" },
          ]
        : [{ value: 415, label: "415 V" }],
    [tention]
  );

  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });

  const selectedPremiseId = values.utilityAddress?.premiseTypeId;

  const maxDateProp = useMemo(
    () =>
      expectedConnectionStartDate
        ? {
            maxDate: moment(expectedConnectionStartDate)
              .add(1, "years")
              .subtract(1, "days"),
          }
        : {},
    [expectedConnectionStartDate]
  );

  useEffect(() => {
    const updatedPremiseIdsForTimeShift = [];
    premiseType?.forEach((premise) => {
      if (premiseNamesForTimeShift.includes(premise.name.toLowerCase())) {
        updatedPremiseIdsForTimeShift.push(premise.id);
      }
    });

    setPremiseIdsForTimeShift(updatedPremiseIdsForTimeShift);
  }, [premiseType]);

  useEffect(() => {
    if (powerRequirement) {
      powerRequirement < minHtContractDemandLoad
        ? setFieldValue("connectionLoad.tention", tentionOptions[0].value)
        : setFieldValue("connectionLoad.tention", tentionOptions[1].value);
    }
  }, [setFieldValue]);

  useEffect(() => {
    if (!isGreenPowerRequired) {
      setFieldValue("connectionLoad.greenPower", "");
      setFieldValue("connectionLoad.averageConsumption", "");
    }
  }, [isGreenPowerRequired, setFieldValue]);

  useEffect(() => {
    const updatedPremiseIds = [];
    premiseType?.forEach((premise) => {
      if (premiseNames.includes(premise.name.toLowerCase())) {
        updatedPremiseIds.push(premise.id);
      }
    });

    setPremiseIds(updatedPremiseIds);
  }, [premiseType]);

  useEffect(() => {
    if (tention === tentionOptions[0].value) {
      if (premiseIds.includes(selectedPremiseId)) {
        setFieldValue(
          "connectionLoad.powerRequirementUnit",
          unitOptions[2].value
        );
      } else {
        setFieldValue(
          "connectionLoad.powerRequirementUnit",
          unitOptions[0].value
        );
      }
    } else {
      setFieldValue(
        "connectionLoad.powerRequirementUnit",
        unitOptions[1].value
      );
    }
  }, [tention, selectedPremiseId, premiseIds, setFieldValue]);

  useEffect(() => {
    if (supplyVoltage) {
      if (supplyVoltage === 415 || supplyVoltage === 240) {
        setFieldValue("connectionLoad.supplyVoltageUnit", 4);
      } else {
        setFieldValue("connectionLoad.supplyVoltageUnit", 5);
      }
    }
  }, [supplyVoltage, setFieldValue]);

  // select time shift value based on premise type and while editing application
  useEffect(() => {
    if (premiseIdsForTimeShift.length) {
      if (timeShift && !premiseIdsForTimeShift.includes(selectedPremiseId)) {
        setFieldValue("connectionLoad.timeShift", "");
      }
      if (premiseIdsForTimeShift.includes(selectedPremiseId) && !timeShift) {
        setFieldValue("connectionLoad.timeShift", timeshiftingOptions[0].value);
      }
    }
  }, [selectedPremiseId, premiseIdsForTimeShift]);

  useEffect(() => {
    const options =
      loadDetails === SINGLE_PHASE
        ? singlePhaseOptions
        : loadDetails === THREE_PHASE
        ? threePhaseOptions
        : [];
    if (
      supplyVoltage &&
      !options.some((option) => option.value === supplyVoltage)
    ) {
      setFieldValue("connectionLoad.supplyVoltage", "");
    }
    setSupplyVoltageOptions(options);
  }, [loadDetails, tention]);

  // for empty the formik value when load options are not matching its value
  useEffect(() => {
    const options =
      tention === tentionOptions[0].value && powerRequirement <= 6
        ? loadOptions
        : htLoadOptions;

    if (!options.some((option) => option.value === loadDetails)) {
      setFieldValue("connectionLoad.loadDetails", "");
    }
  }, [tention, powerRequirement]);
  return (
    <>
      <Typography className="mb-3" lineHeight="1" variant="h6">
        {t("Connection Load")}
      </Typography>

      <Row>
        <Col md={4}>
          <SelectField
            label={t("Power Requirements") + "*"}
            name="connectionLoad.tention"
            formik={formik}
            options={tentionOptions}
            placeholder="Tention"
          />
        </Col>

        <Col md={4}>
          <InputFieldText
            label={
              premiseIds.includes(selectedPremiseId)
                ? tention === tentionOptions[0].value
                  ? t("Contract Load") + "*"
                  : t("Contract Demand") + "*"
                : powerRequirement < minHtContractDemandLoad
                ? t("Contract Load") + "*"
                : t("Contract Demand") + "*"
            }
            name="connectionLoad.powerRequirement"
            formik={formik}
            type="number"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {tention === tentionOptions[0].value &&
                  premiseIds.includes(selectedPremiseId)
                    ? powerRequirement <= 125
                      ? unitOptions[2].label
                      : unitOptions[1].label
                    : powerRequirement < 151
                    ? unitOptions[0].label
                    : unitOptions[1].label}
                </InputAdornment>
              ),
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            placeholder={t("Enter Contract Load")}
          />
        </Col>

        <Col md={4}>
          <SelectField
            label={t("Load Details") + "*"}
            name="connectionLoad.loadDetails"
            formik={formik}
            options={
              tention
                ? tention === tentionOptions[0].value && powerRequirement <= 6
                  ? loadOptions
                  : htLoadOptions
                : []
            }
            placeholder="Select Supply Voltage"
            disabled={!tention}
          />
        </Col>

        <Col md={4}>
          <SelectField
            label={t("Supply Voltage") + "*"}
            name="connectionLoad.supplyVoltage"
            formik={formik}
            disabled={!(powerRequirement && loadDetails)}
            options={supplyVoltageOptions}
            placeholder="Select Supply Voltage"
          />
        </Col>

        {premiseIdsForTimeShift.includes(selectedPremiseId) && (
          <Col md={4}>
            <SelectField
              label="Time shift*"
              name="connectionLoad.timeShift"
              formik={formik}
              options={timeshiftingOptions || []}
              placeholder="Select time shift"
            />
          </Col>
        )}

        <Col md={4}>
          {
            <DateField
              label={t("Connection Start Date") + "*"}
              name="connectionLoad.expectedConnectionStartDate"
              formik={formik}
              minDate={tomorrow}
              format="DD-MM-YYYY"
              maxDate={expectedConnectionEndDate ?? ""}
              placeholder="Select Connection Start Date"
              disablePast
            />
          }
        </Col>
        <Col md={4}>
          {
            <DateField
              label={t("Connection End Date") + "*"}
              name="connectionLoad.expectedConnectionEndDate"
              formik={formik}
              format="DD-MM-YYYY"
              minDate={expectedConnectionStartDate ?? tomorrow}
              placeholder="Select Connection End Date"
              disablePast
              {...maxDateProp}
            />
          }
        </Col>
      </Row>
      <Row>
        <Col md={4} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isGreenPowerRequired}
                onChange={() =>
                  setFieldValue(
                    "connectionLoad.isGreenPowerRequired",
                    !isGreenPowerRequired
                  )
                }
              />
            }
            label={t("Green Power")}
          />
        </Col>

        {isGreenPowerRequired && (
          <>
            <Col md={4}>
              <InputFieldText
                label={t("Green Power") + "*"}
                name="connectionLoad.greenPower"
                formik={formik}
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                }}
                placeholder={t("Enter Green Power")}
              />
            </Col>

            <Col md={4}>
              <InputFieldText
                label={t("Average Consumption") + "*"}
                name="connectionLoad.averageConsumption"
                formik={formik}
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">KWH</InputAdornment>
                  ),
                  inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                }}
                placeholder={t("Enter Average Consumption")}
              />
            </Col>
            <Col xs={12}>
              <Typography color="error" variant="body2" className="mt-4 ms-3">
                {t(
                  "*Opting green power will lead to additional charges as per the latest tariff order."
                )}
              </Typography>
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

ConnectionLoadDetails.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(ConnectionLoadDetails);
